// Tela de criacao de cotacao: abre a cotacao com os produtos e os fornecedores convidados.
// O preenchimento dos precos e a comparacao acontecem ao abrir a cotacao (CotacaoRespostas).
// Layout espelha o ControleFC: labels empilhadas no header, secoes com titulo
// MAIUSCULO + contador, coluna Foto na lista de produtos.
import { useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { Modal } from '../../components/Modal';
import { FotoProduto } from '../../components/FotoProduto';
import { EstoqueEfetivo } from '../../components/EstoqueEfetivo';

interface Produto {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  unidade_sigla: string | null;
  unidade_compra: string | null;
  foto_url: string | null;
  estoque_atual: string | null;
  estoque_efetivo: string | null;
  status?: string | null;
}

interface Fornecedor {
  id: number;
  razao_social: string | null;
  nome_fantasia: string | null;
  cidade?: string | null;
  uf?: string | null;
}

/** Item da prateleira NOVOS PRODUTOS (DA-606) — `GET /api/novos-produtos/busca`. */
interface NovoProdutoBusca {
  id: number;
  codigo: string;
  descricao: string;
  marca: string | null;
  embalagem: string | null;
  foto_url: string | null;
}

interface ItemCot {
  // NULL quando a linha vem da prateleira Novos Produtos: ali nao ha produto do cadastro, e a
  // identidade e o `novo_produto_id`. ⚠️ Os dois viajam pro backend no salvar.
  produto_id: number | null;
  novo_produto_id?: number | null;
  codigo_interno: string | null;
  descricao: string;
  unidade_sigla: string | null;
  foto_url: string | null;
  quantidade: string;
}

export function CotacaoNovaSimples() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();

  const hojeISO = new Date().toISOString().slice(0, 10);
  const [dataValidade, setDataValidade] = useState(hojeISO);
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<ItemCot[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [salvando, setSalvando] = useState(false);

  const [buscaProd, setBuscaProd] = useState('');
  const [resultProd, setResultProd] = useState<Produto[]>([]);
  // A segunda secao do dropdown: a prateleira Novos Produtos (DA-606). A cotacao NASCE aqui, entao
  // sem isto o recurso pareceria quebrado justo no ponto de partida — o item so entraria depois de
  // criar a cotacao vazia e abrir a tela de respostas.
  const [resultNovos, setResultNovos] = useState<NovoProdutoBusca[]>([]);
  const buscaProdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal de selecao de fornecedores (padrao ControleFC: lista + checkboxes).
  // Busca vai ao BACKEND (debounced): a lista de ativos passa do teto de 500 e o
  // filtro client-side so enxergava a fatia alfabetica baixada (UNIVERSO/U ficava
  // de fora). Espelha NotaEntradaImportar (busca por query no servidor).
  const [modalFornAberto, setModalFornAberto] = useState(false);
  const [todosForn, setTodosForn] = useState<Fornecedor[]>([]);
  const [carregandoForn, setCarregandoForn] = useState(false);
  const [buscaModalForn, setBuscaModalForn] = useState('');
  const buscaFornTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Busca produto debounced
  useEffect(() => {
    if (buscaProdTimer.current) clearTimeout(buscaProdTimer.current);
    if (!buscaProd.trim()) { setResultProd([]); setResultNovos([]); return; }
    buscaProdTimer.current = setTimeout(async () => {
      try {
        const [r, n] = await Promise.all([
          api.get<Produto[]>('/api/produtos', { query: { q: buscaProd, com_inativos: '1' } }),
          api.get<NovoProdutoBusca[]>('/api/novos-produtos/busca', { query: { q: buscaProd } })
            .catch(() => [] as NovoProdutoBusca[]),
        ]);
        setResultProd((r || []).slice(0, 15));
        setResultNovos((n || []).slice(0, 15));
      } catch { setResultProd([]); setResultNovos([]); }
    }, 250);
    return () => { if (buscaProdTimer.current) clearTimeout(buscaProdTimer.current); };
  }, [buscaProd]);

  function adicionarProduto(p: Produto) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    if (itens.some((it) => it.produto_id === p.id)) {
      toast.aviso('Produto ja esta na lista');
      return;
    }
    setItens((arr) => [...arr, {
      produto_id: p.id,
      codigo_interno: p.codigo_interno,
      descricao: p.descricao,
      unidade_sigla: p.unidade_compra || p.unidade_sigla,
      foto_url: p.foto_url,
      quantidade: '1',
    }]);
    setBuscaProd('');
    setResultProd([]);
    setResultNovos([]);
  }

  /** Acrescenta a lista um item da prateleira Novos Produtos (DA-606). */
  function adicionarNovoProduto(n: NovoProdutoBusca) {
    // A duplicata se checa pela IDENTIDADE dele — `produto_id` e null aqui e nao distingue nada.
    if (itens.some((it) => it.novo_produto_id === n.id)) {
      toast.aviso('Este item ja esta na lista');
      return;
    }
    setItens((arr) => [...arr, {
      produto_id: null,
      novo_produto_id: n.id,
      codigo_interno: n.codigo,
      descricao: n.descricao,
      // A EMBALAGEM e' a unidade do item da prateleira ([DA-608] p.4) — nao ha mais campo separado.
      unidade_sigla: n.embalagem || 'UN',
      foto_url: n.foto_url,
      quantidade: '1',
    }]);
    setBuscaProd('');
    setResultProd([]);
    setResultNovos([]);
  }

  // Abre o modal. A lista vem do backend pelo termo de busca (efeito abaixo).
  function abrirModalForn() {
    setModalFornAberto(true);
  }

  // Busca fornecedores no SERVIDOR (debounced) sempre que o modal esta aberto.
  // Sem termo, traz a lista default de ativos; com termo, o banco filtra por
  // razao/fantasia/cnpj/telefone (alem do teto de 500).
  useEffect(() => {
    if (!modalFornAberto) return;
    if (buscaFornTimer.current) clearTimeout(buscaFornTimer.current);
    const termo = buscaModalForn.trim();
    buscaFornTimer.current = setTimeout(async () => {
      try {
        setCarregandoForn(true);
        const r = await api.get<Fornecedor[]>('/api/fornecedores', {
          query: { busca: termo || undefined },
        });
        setTodosForn(r);
      } catch {
        toast.erro('Erro ao carregar fornecedores');
      } finally { setCarregandoForn(false); }
    }, 250);
    return () => { if (buscaFornTimer.current) clearTimeout(buscaFornTimer.current); };
  }, [modalFornAberto, buscaModalForn]);

  function toggleFornecedor(f: Fornecedor) {
    setFornecedores((arr) =>
      arr.some((x) => x.id === f.id) ? arr.filter((x) => x.id !== f.id) : [...arr, f],
    );
  }

  // Backend ja devolve filtrado pelo termo — sem filtro client-side (evita o teto de 500).
  const fornFiltrados = todosForn;

  async function salvar() {
    if (itens.length === 0) {
      toast.erro('Adicione pelo menos um produto');
      return;
    }
    if (fornecedores.length === 0) {
      toast.erro('Adicione pelo menos um fornecedor convidado');
      return;
    }
    try {
      setSalvando(true);
      const body = {
        data_validade: dataValidade || null,
        observacoes: observacoes.trim() ? observacoes.trim().toUpperCase() : null,
        itens: itens.map((it) => ({
          produto_id: it.produto_id,
          // ⚠️ Item da prateleira Novos Produtos viaja com a identidade DELE e com o nome: sem os
          // dois, a linha nasce sem produto e sem descricao, e a grade a desenha EM BRANCO.
          novo_produto_id: it.novo_produto_id ?? null,
          descricao_livre: it.produto_id == null ? it.descricao : null,
          quantidade: parseFloat(it.quantidade.replace(',', '.')) || 1,
          unidade_sigla: it.unidade_sigla,
        })),
        fornecedor_ids: fornecedores.map((f) => f.id),
      };
      const r = await api.post<{ id: number; numero: string }>('/api/cotacoes', body);
      toast.sucesso(`Cotacao ${r.numero} criada`);
      navigate('/cotacoes');
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 500, color: 'var(--text-muted, #6b7280)', marginBottom: 2,
  };
  const tituloSecaoStyle: React.CSSProperties = {
    margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: 0.3, color: 'var(--text-color)',
  };

  return (
    <div className="modulo-pagina">
      {/* Header sticky inline — labels empilhadas sobre os campos (padrao ControleFC) */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--page-bg)', borderBottom: '1px solid var(--page-border, #e5e7eb)', padding: '10px 16px' }}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            Nova Cotacao
          </h2>

          <div className="pagina-acoes">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={labelStyle}>Validade</span>
              <input
                type="date"
                value={dataValidade}
                onChange={(e) => setDataValidade(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--input-border, #d1d5db)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 260 }}>
              <span style={labelStyle}>Observacoes</span>
              <input
                type="text"
                placeholder="Notas internas..."
                value={observacoes}
                onChange={(e) => setObservacoes(upperMantendoCursor(e))}
                style={{ padding: '6px 8px', border: '1px solid var(--input-border, #d1d5db)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)' }}
              />
            </div>

            <button type="button" onClick={() => navigate(-1)} className="btn-secundario" disabled={salvando}>
              Cancelar
            </button>
            <button type="button" onClick={salvar} className="btn-primario" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
        {/* Coluna 1: Produtos */}
        <section style={{ background: 'var(--panel-bg)', borderRadius: 6, padding: 12, border: '1px solid var(--panel-border, #e5e7eb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <h2 style={tituloSecaoStyle}>PRODUTOS DA COTACAO ({itens.length})</h2>
          </div>

          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={14} style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted, #6b7280)' }} />
            <input
              type="text"
              placeholder="Buscar por nome, codigo, EAN ou codigo NP para adicionar..."
              value={buscaProd}
              onChange={(e) => setBuscaProd(e.target.value)}
              style={{ width: '100%', padding: '6px 8px 6px 28px', border: '1px solid var(--input-border, #d1d5db)', borderRadius: 4, background: 'var(--input-bg)', color: 'var(--text-color)', fontSize: 13 }}
            />
            {(resultProd.length > 0 || resultNovos.length > 0) && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--popup-bg)', border: '1px solid var(--panel-border, #e5e7eb)', borderRadius: 4, maxHeight: 280, overflowY: 'auto', zIndex: 5, marginTop: 2 }}>
                {resultProd.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => adicionarProduto(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: 13 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--grid-row-hover-bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {p.foto_url ? (
                      <FotoProduto url={p.foto_url} tam={36} style={{ flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 4, background: 'var(--grid-header-bg)', border: '1px solid var(--panel-border, #e5e7eb)', flexShrink: 0 }} />
                    )}
                    <span>{produtoInativo(p) && <SeloInativo />}<strong>{p.codigo_interno}</strong> — {p.descricao}</span>
                    <span style={{ flexShrink: 0, marginLeft: 16, fontWeight: 700 }}>
                      Estoque:{' '}
                      <EstoqueEfetivo
                        proprio={p.estoque_atual == null ? null : Number(p.estoque_atual)}
                        efetivo={p.estoque_efetivo == null ? null : Number(p.estoque_efetivo)}
                      />
                      {p.unidade_sigla ? ` ${p.unidade_sigla}` : ''}
                    </span>
                  </button>
                ))}

                {/* A seção NOVOS PRODUTOS, por último — o item que ainda não é de ninguém. */}
                {resultNovos.length > 0 && (
                  <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: 'var(--text-muted)', background: 'var(--grid-row-alt-bg)', borderTop: resultProd.length > 0 ? '1px solid var(--panel-border, #e5e7eb)' : 'none' }}>
                    NOVOS PRODUTOS — AINDA NÃO EXISTEM NO CADASTRO
                  </div>
                )}
                {resultNovos.map((n) => (
                  <button
                    key={`np-${n.id}`}
                    type="button"
                    onClick={() => adicionarNovoProduto(n)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: 13 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--grid-row-hover-bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {n.foto_url ? (
                      <FotoProduto url={n.foto_url} tam={36} style={{ flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 4, background: 'var(--grid-header-bg)', border: '1px solid var(--panel-border, #e5e7eb)', flexShrink: 0 }} />
                    )}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{n.codigo}</strong> — {n.descricao}
                      {n.marca ? <span style={{ color: 'var(--text-muted)' }}> · {n.marca}</span> : null}
                    </span>
                    <span style={{ backgroundColor: '#c4b5fd', color: '#000', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                      NOVO
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 48 }}>Foto</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 60 }}>Cod.</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600 }}>Produto</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 60 }}>Un.</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, width: 80 }}>Qtd.</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Nenhum produto adicionado.</td></tr>
              )}
              {itens.map((it, idx) => (
                <tr key={it.produto_id} style={{ borderTop: '1px solid var(--grid-row-border, #f3f4f6)' }}>
                  <td style={{ padding: '4px 8px' }}>
                    {it.foto_url ? (
                      <FotoProduto url={it.foto_url} tam={36} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 4, background: 'var(--grid-header-bg)', border: '1px solid var(--panel-border, #e5e7eb)' }} />
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted, #6b7280)' }}>{it.codigo_interno}</td>
                  <td style={{ padding: '6px 8px' }}>{it.descricao}</td>
                  <td style={{ padding: '6px 8px' }}>{it.unidade_sigla || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={it.quantidade}
                      onChange={(e) => setItens((arr) => arr.map((x, i) => i === idx ? { ...x, quantidade: e.target.value } : x))}
                      style={{ width: 60, padding: 2, textAlign: 'right', border: '1px solid var(--input-border, #d1d5db)', borderRadius: 3, background: 'var(--input-bg)', color: 'var(--text-color)' }}
                    />
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setItens((arr) => arr.filter((x) => x.produto_id !== it.produto_id))}
                      style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}
                      title="Remover"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Coluna 2: Fornecedores */}
        <section style={{ background: 'var(--panel-bg)', borderRadius: 6, padding: 12, border: '1px solid var(--panel-border, #e5e7eb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <h2 style={tituloSecaoStyle}>FORNECEDORES CONVIDADOS ({fornecedores.length})</h2>
            <button
              type="button"
              onClick={abrirModalForn}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: 'var(--primary, #2563eb)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}
            >
              <Plus size={15} /> Selecionar
            </button>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {fornecedores.length === 0 && (
              <li style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted, #6b7280)', fontSize: 13 }}>
                Nenhum fornecedor selecionado.
              </li>
            )}
            {fornecedores.map((f) => (
              <li key={f.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderTop: '1px solid var(--grid-row-border, #f3f4f6)', fontSize: 13 }}>
                <span style={{ flex: 1 }}>{f.nome_fantasia || f.razao_social}</span>
                <button
                  type="button"
                  onClick={() => setFornecedores((arr) => arr.filter((x) => x.id !== f.id))}
                  style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}
                  title="Remover"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Modal de selecao de fornecedores — padrao ControleFC: lista completa + checkboxes */}
      <Modal
        aberto={modalFornAberto}
        onFechar={() => setModalFornAberto(false)}
        titulo={`Fornecedores Convidados (${fornecedores.length})`}
        largura={520}
        rodape={
          <button type="button" className="btn-secundario" onClick={() => setModalFornAberto(false)}>
            Fechar
          </button>
        }
      >
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted, #6b7280)' }} />
          <input
            type="text"
            autoFocus
            placeholder="Pesquisar fornecedor..."
            value={buscaModalForn}
            onChange={(e) => setBuscaModalForn(e.target.value)}
            style={{ width: '100%', padding: '8px 10px 8px 34px', border: '1px solid var(--input-border, #d1d5db)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)', fontSize: 14 }}
          />
        </div>

        {carregandoForn && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</div>
        )}
        {!carregandoForn && fornFiltrados.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
            Nenhum fornecedor encontrado.
          </div>
        )}
        {!carregandoForn && fornFiltrados.map((f) => {
          const marcado = fornecedores.some((x) => x.id === f.id);
          const local = [f.cidade, f.uf].filter(Boolean).join('/');
          return (
            <label
              key={f.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 6px', borderBottom: '1px solid var(--grid-row-border, #f3f4f6)',
                cursor: 'pointer',
                background: marcado ? 'var(--menu-ativo-bg, #dbeafe)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={marcado}
                onChange={() => toggleFornecedor(f)}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: 500 }}>{f.nome_fantasia || f.razao_social}</span>
                {local && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>{local}</span>
                )}
              </span>
            </label>
          );
        })}
      </Modal>
    </div>
  );
}
